/**
 * KpiGroupWidget — renders a responsive row of KpiWidgets for a set of metric
 * keys, fetching them in one /api/dashboard/v2/kpis call scoped by the shared
 * DashboardContext (module/site/time/department). Config-driven: a dashboard
 * just lists metric keys.
 */
import React, { useState } from 'react';
import { useDashboard } from '../context/DashboardContext';
import { useKpis } from '../hooks/useDashboardData';
import KpiWidget from './KpiWidget';
import DrilldownModal from './DrilldownModal';
import { LABEL, ACCENT_DOT } from '../ui';
import { isSiteRequiredError, SiteRequiredNotice } from '../utils/siteScope';

const COLS = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
};

/**
 * KPI group. Either a flat list (`metrics`) or labeled, color-coded
 * `sections` ([{ label, accent, metrics:[keys] }]) for a scannable layout.
 *
 * @param drill  'modal' (default) opens a records grid for the clicked KPI;
 *               'none' disables drill-down. A custom `onDrill(key, value)`
 *               callback overrides both.
 */
export default function KpiGroupWidget({
  metrics, sections, columns = 4, accents = {}, onDrill, drill = 'modal',
}) {
  const { module, siteId, time, departmentId, dimensions, fyStartMonth, compare, standard, refreshMs } = useDashboard();

  // Normalize to sections; a flat `metrics` list becomes a single untitled section.
  const secs = sections && sections.length
    ? sections
    : [{ label: null, accent: null, metrics: metrics || [] }];
  const allMetrics = secs.flatMap((s) => s.metrics);

  const { metrics: values, loading, unauthorized, error } = useKpis({
    module, metrics: allMetrics, siteId, time, departmentId, dimensions, fyStartMonth, compare, standard, refreshMs,
  });

  const [drilled, setDrilled] = useState(null);
  const unauthSet = new Set(unauthorized);
  const colCls = columns in COLS ? COLS[columns] : COLS[4];

  const drillEnabled = onDrill || drill === 'modal';
  // A metric with no drill spec has no records to list; making its card look
  // clickable only leads to an error, so it stays inert.
  const isClickable = (key) => drillEnabled && values[key]?.drillable !== false;
  const handleClick = (key) => {
    if (onDrill) return onDrill(key, values[key]);
    if (drill === 'modal') setDrilled({ key, label: values[key]?.label || key });
    return undefined;
  };

  // Without this the strip rendered a card per metric, each reading "no data"
  // — indistinguishable from a site that genuinely has no incidents.
  if (isSiteRequiredError(error)) return <SiteRequiredNotice compact />;

  return (
    <div className="space-y-5">
      {secs.map((sec, si) => {
        const keys = sec.metrics.filter((k) => !unauthSet.has(k));
        if (keys.length === 0) return null;
        return (
          <div key={sec.label || si}>
            {sec.label && (
              <div className="mb-2 flex items-center gap-2">
                {sec.accent && <span className={`h-1.5 w-1.5 rounded-full ${ACCENT_DOT[sec.accent] || ACCENT_DOT.blue}`} />}
                <span className={LABEL}>{sec.label}</span>
                <span className="h-px flex-1 bg-gray-100" />
              </div>
            )}
            <div className={`grid grid-cols-1 gap-4 ${colCls}`}>
              {keys.map((key) => (
                <KpiWidget
                  key={key}
                  loading={loading && !values[key]}
                  metric={values[key]}
                  accent={sec.accent || accents[key]}
                  trend={values[key]?.trend ?? null}
                  trendLabel="vs previous period"
                  onClick={isClickable(key) ? () => handleClick(key) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}
      <DrilldownModal
        open={!!drilled}
        metric={drilled?.key}
        title={drilled?.label}
        onClose={() => setDrilled(null)}
      />
    </div>
  );
}
